/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import { Tooltip } from '@material-tailwind/react';
import {
  LeadCapturePage_attributeList,
  LeadCapturePage_gift,
  RewardProduct_Detail,
} from '__generated__/graphql';
import { JSX, useState } from 'react';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import {
  trackLeadCaptureClick,
  MembershipRewardsTrackingData,
} from 'utils/tracking';
import { RewardOfferText } from './i18n';
import { isInsuranceProduct } from './insuranceVerticals';
import {
  CatalogueProductOffers,
  getHighestGiftValue,
  getOffersBasedOnCriteria,
} from './rewardOffersUtils';
import { RewardProductDialog } from './RewardProductDialog';
import Diamond from '$/components/icons/Diamond';

export function RewardOffers(props: {
  product: RewardProduct_Detail;
  attributes: LeadCapturePage_attributeList['attributeList'];
  offers: LeadCapturePage_gift[];
  staticText: Translated<typeof RewardOfferText>;
  loanAmount?: number;
  currencyDisplay: string;
}): JSX.Element {
  const [showProductDetail, setShowProductDetail] = useState<boolean>(false);
  const [scrollToSection, setScrollToSection] = useState<string | null>(null);

  /*offers rewards*/
  const allActiveOffers = getOffersBasedOnCriteria(
    props.product?.catalogueProduct as CatalogueProductOffers,
    props.product.productVertical?.code === 'ULNS'
      ? props.loanAmount
      : undefined,
  );

  const highestGiftValue = getHighestGiftValue(allActiveOffers);

  // Insurance rewards are largely lucky-draw entries rather than a guaranteed
  // gift the user picks, so insurance verticals get their own subheading.
  const offerSubHeading = isInsuranceProduct(props.product)
    ? props.staticText.offerSubHeadingINS
    : props.staticText.offerSubHeading;

  const {
    dialogSeeMore,
    dialogSeeLess,
    dialogRewardsTitle,
    dialogRewardsDescription,
    dialogRewardsOption,
    dialogRewardsTC,
    eligibility,
    fees,
    worthText,
  } = props.staticText;

  const tracking: MembershipRewardsTrackingData = {
    email: '',
    campaignId: props.product.productId,
    campaignName: props.product.productDisplayName,
    product: {
      productUid: props.product?.catalogueProduct?.productUid,
      productName: props.product?.catalogueProduct?.productName,
      providerName: props.product?.catalogueProduct?.provider?.providerName,
    },
  };

  return (
    <section className="mhg-alpha-rewards-lead-capture-section">
      <div className="mhg-alpha-rewards-lead-capture-heading-container">
        <div className="mhg-alpha-display-1 not-italic text-[--text-primary]">
          {highestGiftValue?.price
            ? t(props.staticText.offerHeadingDynamic, {
                giftValue:
                  props.currencyDisplay +
                  Number(highestGiftValue.price).toLocaleString('en-SG', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }),
              })
            : props.staticText.offerHeading}
        </div>
        <div className="mhg-alpha-rewards-lead-capture-subheading">
          {t(offerSubHeading, {
            verticalName:
              props.product?.productVertical?.name
                ?.toLocaleLowerCase()
                ?.replace(/Unsecured\s+/i, '')
                ?.replace(/Secured\s+/i, '')
                ?.replace(/s$/, '') ?? 'Application',
          })}
        </div>
      </div>
      <div className="mhg-alpha-rewards-lead-capture-card bg-[--fill-card]">
        <div className="mhg-alpha-rewards-lead-capture-offer-body md:px-[--spacing-padding-x] md:py-[--spacing-padding-y]">
          {allActiveOffers.length > 0 && (
            <div className="mhg-alpha-rewards-lead-capture-offer-content mhg-alpha-rewards-lead-capture-offer-content-no-scrollbar md:mhg-alpha-rewards-lead-capture-offer-content-md max-w-[calc(100vw-35px)] md:max-w-full">
              <div className="mhg-alpha-rewards-lead-capture-offer-content-no-scrollbar flex content-center items-center gap-[--spacing-gap-x-sm] self-stretch overflow-x-scroll whitespace-nowrap md:grid md:grid-cols-4 md:gap-x-[--spacing-gap-x-sm] md:gap-y-[--spacing-gap-y-sm] lg:grid-cols-5">
                {allActiveOffers.map((offer) =>
                  offer.gifts?.length
                    ? offer.gifts.map((gift) => (
                        <div
                          key={gift.giftUid}
                          className="flex h-24 w-30 flex-1 flex-col items-center justify-end rounded-[--radius-button] border border-solid border-[--borders-default] bg-[--fill-card] first:ml-spacing/margin/x last:mr-spacing/margin/x md:h-34 md:w-auto md:first:ml-0 md:last:mr-0"
                        >
                          <div className="flex h-19 items-center justify-center gap-[10px] self-stretch p-[--button-padding-regular-x-icon] md:h-27">
                            <Tooltip
                              className="flex max-w-[--general-4-col-width] items-end justify-center rounded-[--radius-tooltip] px-[--spacing-padding-x-sm] py-[--spacing-padding-y-sm] shadow-md bg-[--fill-card]"
                              placement="top-start"
                              content={
                                <div>
                                  <span className="text-text/secondary-lighter">
                                    {offer.offerTitle}
                                  </span>
                                  <br></br>
                                  <span className="font-semibold text-text/primary">
                                    {gift.giftTitle}
                                  </span>
                                </div>
                              }
                            >
                              <img
                                className="self-stretch object-contain"
                                src={gift.mobileImage}
                                alt={gift.giftTitle}
                              />
                            </Tooltip>
                          </div>
                          <div className="flex items-center justify-center self-stretch overflow-hidden rounded-b-[inherit] px-[--spacing-padding-x-xs] py-[--button-padding-toggle] text-center bg-[--fill-alternative-1] sm:min-w-30">
                            <span className="mhg-alpha-rewards-lead-capture-offer-image-title-text w-full truncate text-[--text-secondary]">
                              {gift.giftValue?.price
                                ? t(worthText, {
                                    giftValue:
                                      props.currencyDisplay +
                                      Number(
                                        gift.giftValue.price,
                                      ).toLocaleString('en-SG', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                      }),
                                  })
                                : gift.giftTitle}
                            </span>
                          </div>
                        </div>
                      ))
                    : null,
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mhg-alpha-rewards-lead-capture-card-footer">
          <button
            type="button"
            className="mhg-alpha-rewards-lead-capture-card-footer-content bg-colors/neutral-white"
            onClick={() => {
              setScrollToSection('rewards');
              setShowProductDetail(true);
              trackLeadCaptureClick(
                'card',
                `Reward Card_See All Eligible Rewards`,
                tracking,
                'lead-submission',
                1,
                'product_interaction',
              );
            }}
          >
            <Diamond className="mhg-alpha-rewards-lead-capture-card-footer-icon" />
            <span className="mhg-alpha-rewards-lead-capture-card-footer-content-label">
              {props.staticText.offerSeeAll} ({allActiveOffers.length})
            </span>
          </button>
        </div>
      </div>
      <RewardProductDialog
        setOpen={setShowProductDetail}
        scrollToSection={scrollToSection}
        isOpen={showProductDetail}
        product={props.product}
        attributes={props.attributes}
        staticText={{
          dialogSeeMore,
          dialogSeeLess,
          dialogRewardsTitle,
          dialogRewardsDescription,
          dialogRewardsOption,
          dialogRewardsTC,
          eligibility,
          fees,
        }}
      />
    </section>
  );
}
